'use client';

import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:chat-log');
import { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import type { DraftAttachment, DynamicSuggestion, PromptTemplate } from '@ctrl-enter/types';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../auth';
import { useDragAndDrop } from '../hooks/use-drag-and-drop';
import { useDynamicSuggestions } from '../hooks/use-dynamic-suggestions';
import { useExternalLinks } from '../hooks/use-external-links';
import { useExtensionDetection } from '../hooks/use-extension-detection';
import { useGetIntegrationsQuery } from '../hooks/use-gateway-queries';
import { useBlockedFeatureModal, useOpenPromptLibrary } from '../hooks/use-platform-aware-modals';
import { ChatMessage } from '../stores/chat-store';
import { setSelectedEnterAccountId } from '../stores/enter-account-store';
import { usePromptLibraryStore } from '../stores/prompt-library-store';
import { useRealTimeModeSuggestedMcpAccountId } from '../stores/real-time-mode-store';
import { processClipboardData } from '../utils/clipboard';
import { toast } from '../utils/toast';
import { cn } from '../utils/cn';
import { getKeyDisplayText, getModifierKey } from '../utils/platform';
import { formatAbsoluteTime, formatRelativeTime } from '../utils/time-format';
import { AttachedFilesList } from './attached-files-list';
import { SkillSelectorButton } from './ai-skills';
// SkillSelectorModal is intentionally NOT rendered here — it must live at the
// chat-view root to escape the backdrop-filter stacking context of the input container.
import { ChatLoadingSkeleton } from './chat-loading-skeleton';
import { FeatureBlockedModal } from './feature-blocked-modal';
import { NewChatHome } from './new-chat-home';
import { StarterPrompts } from './starter-prompts/starter-prompts';
import { FilePreview } from './file-preview';
import { IconButton } from './icon-button';
import { Icon } from './icons/icon';
import { ImagePreviewModal } from './image-preview-modal';
import { McpSelect } from './mcp-select';
import { PromptDropdownMenu } from './prompt-dropdown-menu';
import { PromptSuggestionsPanel } from './prompt-suggestions-panel';
import { PromptVariableDialog } from './prompt-variable-dialog';
import { StreamingExplanationTooltip } from './streaming-explanation-tooltip';
import { Textarea } from './textarea';
import { Tooltip } from './tooltip';
import { TypingIndicator } from './typing-indicator';
import { adjustWindowHeight } from '../utils/window-visibility';
import { VoiceMessageControls } from './voice-message-controls';
import { VoiceRecorderButton, type VoiceRecorderButtonHandle } from './voice-recorder-button';
import { useAutoScroll } from '../stores/system-settings-store';

/**
 * Constants for textarea height
 * - TEXTAREA_MIN_HEIGHT: Minimum height in pixels
 * - TEXTAREA_MAX_HEIGHT: Maximum height in pixels
 */
const TEXTAREA_MIN_HEIGHT = 80;
const TEXTAREA_MAX_HEIGHT = 240;
const WINDOW_MIN_HEIGHT_ADJUSTMENT = 160;

// Hook to efficiently select attachment data for all messages
// Simplified to handle only S3 URLs - no base64 logic
const useMessageAttachments = (
  messages: ChatMessage[],
  sessionId?: string
): Map<string, ProcessedAttachment[]> => {
  return React.useMemo(() => {
    const attachmentMap = new Map<string, ProcessedAttachment[]>();

    if (messages.length === 0) {
      return attachmentMap;
    }

    // Process attachments for each message
    messages.forEach(message => {
      const processedAttachments: ProcessedAttachment[] = [];

      // 1. Handle S3 chat history messages with attachedFiles (presigned URLs from S3)
      if ((message as any).attachedFiles?.length) {
        (message as any).attachedFiles.forEach((fileUrl: string, index: number) => {
          if (fileUrl && fileUrl.startsWith('http')) {
            const fileName = extractFileNameFromUrl(fileUrl) || `Attachment ${index + 1}`;
            const contentType = getContentTypeFromFileName(fileName);

            const processedAttachment = {
              id: `s3-attached-${message.id}-${index}`,
              name: fileName,
              contentType,
              url: fileUrl, // Use presigned URL directly
            };

            processedAttachments.push(processedAttachment);
          } else {
            logger.warn(
              `⚠️ [useMessageAttachments] Skipping invalid attachedFile at index ${index}:`,
              fileUrl
            );
          }
        });
      }

      // 2. Handle screenshots with S3 URLs only
      if (message.screenshots?.length) {
        logger.info(
          `📸 [useMessageAttachments] Processing ${message.screenshots.length} screenshots for message ${message.id}`
        );
        message.screenshots.forEach((screenshot, index) => {
          if ((screenshot as any).url && (screenshot as any).url.startsWith('http')) {
            const processedAttachment = {
              id: `s3-screenshot-${message.id}-${index}`,
              name: screenshot.filename || `Screenshot ${index + 1}`,
              contentType: screenshot.mimeType,
              url: (screenshot as any).url,
            };

            logger.info(`✅ [useMessageAttachments] Added screenshot attachment:`, {
              id: processedAttachment.id,
              name: processedAttachment.name,
              contentType: processedAttachment.contentType,
            });

            processedAttachments.push(processedAttachment);
          }
        });
      }

      // 3. Handle other attachments with S3 URLs only
      if (message.attachments?.length) {
        logger.info(
          `📁 [useMessageAttachments] Processing ${message.attachments.length} other attachments for message ${message.id}`
        );
        message.attachments.forEach((attachment, index) => {
          if ((attachment as any).url && (attachment as any).url.startsWith('http')) {
            const processedAttachment = {
              id: `s3-attachment-${message.id}-${index}`,
              name: attachment.filename || `Attachment ${index + 1}`,
              contentType: attachment.mimeType,
              url: (attachment as any).url,
            };

            logger.info(`✅ [useMessageAttachments] Added other attachment:`, {
              id: processedAttachment.id,
              name: processedAttachment.name,
              contentType: processedAttachment.contentType,
            });

            processedAttachments.push(processedAttachment);
          }
        });
      }

      if (processedAttachments.length > 0) {
        attachmentMap.set(message.id, processedAttachments);
      }
    });
    return attachmentMap;
  }, [messages, sessionId]);
};

// Helper functions for S3 file URL processing
function extractFileNameFromUrl(url: string): string | null {
  try {
    // Extract filename from URL path
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    let fileName = pathParts[pathParts.length - 1] || null;

    // Handle URL-encoded query parameters in filename
    // For S3 presigned URLs, the filename might have %3F (encoded ?) in it
    if (fileName && fileName.includes('%3F')) {
      fileName = fileName.split('%3F')[0] ?? null; // Take only the part before the encoded ?
    }
    return fileName;
  } catch {
    // If URL parsing fails, try to extract from the end of the string
    const parts = url.split('/');
    let fileName = parts[parts.length - 1] || null;

    // Handle URL-encoded query parameters in filename
    if (fileName && fileName.includes('%3F')) {
      fileName = fileName.split('%3F')[0] ?? null;
    }

    logger.info('🔍 [extractFileNameFromUrl] Fallback extraction:', { url, fileName });
    return fileName;
  }
}

function getContentTypeFromFileName(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();

  let contentType;
  switch (extension) {
    case 'png':
      contentType = 'image/png';
      break;
    case 'jpg':
    case 'jpeg':
      contentType = 'image/jpeg';
      break;
    case 'gif':
      contentType = 'image/gif';
      break;
    case 'webp':
      contentType = 'image/webp';
      break;
    case 'pdf':
      contentType = 'application/pdf';
      break;
    case 'txt':
      contentType = 'text/plain';
      break;
    case 'json':
      contentType = 'application/json';
      break;
    case 'doc':
      contentType = 'application/msword';
      break;
    case 'docx':
      contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      break;
    case 'rtf':
      contentType = 'application/rtf';
      break;
    case 'md':
    case 'markdown':
      contentType = 'text/markdown';
      break;
    case 'csv':
      contentType = 'text/csv';
      break;
    default:
      contentType = 'application/octet-stream';
      break;
  }

  return contentType;
}

// Processed attachment data for efficient rendering
export type ProcessedAttachment = {
  id: string;
  name: string;
  contentType: string;
  url: string; // base64 data URL
};

export type ChatLogProps = {
  messages: ChatMessage[];
  sessionId?: string; // Add sessionId to retrieve images from bucket store
  targetMessageId?: string | null; // For scrolling to specific message from search
  value: string;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isLoadingMessages?: boolean; // NEW: Loading messages from history
  showEmptyState?: boolean; // NEW: Show empty state instead of messages
  className?: string;
  hideCloseButton?: boolean;
  hasPendingScreenshot?: boolean;
  attachedFiles?: DraftAttachment[]; // Already confirmed attachments
  isProcessingFiles?: boolean;
  isCapturingScreen?: boolean;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onUpload?: () => void;
  onCapture?: () => void;
  onCapturePageHtml?: () => void;
  isCapturingPage?: boolean;
  draftPageContext?: {
    title: string;
    url: string;
    charCount: number;
    wasTruncated: boolean;
  } | null;
  onClearPageContext?: () => void;
  onClose?: () => void;
  onRemoveAttachment?: (index: number) => void;
  onFilesDrop?: (files: File[]) => void;
  onFilesPaste?: (files: File[]) => void;
  onGoToIntegrations?: () => void;
  onRegenerate?: () => void;
  onRetry?: (messageId: string) => void;
  // AI Skills
  selectedSkill?: import('@ctrl-enter/types/ai-skills').AiSkillPublic | null;
  onOpenSkillSelector?: () => void;
};

const MessageBubble: React.FC<{
  message: ChatMessage;
  attachments?: ProcessedAttachment[];
  onImageClick?: (imageUrl: string, imageName: string) => void;
  onFileClick?: (file: ProcessedAttachment) => void;
  onLinkClick?: (href: string) => void;
  isHighlighted?: boolean;
  onRef?: (el: HTMLDivElement | null) => void;
  showTimestamp?: boolean;
  sessionId?: string | undefined;
  isLastAssistant?: boolean;
  onRegenerate?: () => void;
  onRetry?: (messageId: string) => void;
}> = ({
  message,
  attachments = [],
  onImageClick,
  onFileClick,
  onLinkClick,
  isHighlighted = false,
  onRef,
  showTimestamp = false,
  sessionId,
  isLastAssistant = false,
  onRegenerate,
  onRetry,
}) => {
  // Handle both role and sender fields for backwards compatibility
  const messageRole = (message as any).role || (message as any).sender;
  const isUser = messageRole === 'user';
  const isSystem = messageRole === 'system';
  const isProcessing = message.status === 'processing';
  const isStreaming = message.status === 'streaming';
  const isAssistant = messageRole === 'assistant' || messageRole === 'ai';

  // Show loading for assistant messages that are processing, streaming with no content, or have empty content while streaming
  const showLoading = isAssistant && (isProcessing || (isStreaming && !message.content?.trim()));

  // Copy-to-clipboard state — hooks must be declared before any early returns
  const [copied, setCopied] = React.useState(false);
  const handleCopy = React.useCallback(() => {
    if (!message.content) return;
    navigator.clipboard.writeText(message.content).catch(() => {
      // Fallback for environments without clipboard API
      const el = document.createElement('textarea');
      el.value = message.content || '';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message.content]);

  if (isSystem) return null; // Don't render system messages in UI

  if (isUser) {
    // Use pre-processed attachments passed from parent
    const fileUploads = attachments;

    return (
      <div
        ref={onRef}
        className={cn(
          'w-full mb-2 group rounded-lg transition-all duration-300 relative',
          isHighlighted && 'bg-primary/10 ring-2 ring-primary/30 shadow-lg'
        )}
      >
        {/* File upload thumbnails above message - only show non-screenshot attachments */}
        {fileUploads.length > 0 && (
          <div className="flex justify-end mb-2">
            <div className="flex gap-1">
              {fileUploads.slice(0, 3).map(file => (
                <FilePreview
                  key={file.id}
                  id={file.id}
                  name={file.name}
                  contentType={file.contentType}
                  url={file.url}
                  size="md"
                  onClick={() => onFileClick?.(file)}
                  showFileName={false}
                />
              ))}
              {fileUploads.length > 3 && (
                <div className="w-12 h-12 rounded-lg glass-surface-bg glass-surface-border shadow-sm flex items-center justify-center text-xs text-muted-foreground font-medium">
                  +{fileUploads.length - 3}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page context chip — shown when a page was captured with this message */}
        {message.pageContext && (
          <div className="flex justify-end mb-1">
            <div className="flex items-center gap-1.5 bg-bg-neutral-7 rounded-lg px-2.5 py-1 text-xs text-text-secondary max-w-[80%]">
              <span className="shrink-0">🌐</span>
              <span className="truncate" title={message.pageContext.url}>
                {message.pageContext.title || message.pageContext.url}
              </span>
              <span className="shrink-0 text-text-tertiary">
                · {(message.pageContext.charCount / 1000).toFixed(1)}k chars
                {message.pageContext.wasTruncated ? ' (truncated)' : ''}
              </span>
            </div>
          </div>
        )}

        <div className="flex w-full justify-end items-end gap-2">
          {/* Hover-reveal timestamp — visible on row hover when no boundary timestamp is shown */}
          {!showTimestamp && message.timestamp && (
            <Tooltip content={formatAbsoluteTime(message.timestamp)} side="top" delay={400}>
              <span
                className="text-[10px] text-muted-foreground/40 whitespace-nowrap
 select-none opacity-0 group-hover:opacity-100 transition-opacity duration-150
 cursor-default pb-2"
              >
                {formatRelativeTime(message.timestamp)}
              </span>
            </Tooltip>
          )}
          <div
            className={cn(
              'max-w-[80%] rounded-2xl px-4 py-3 text-sm text-foreground shadow-sm border',
              message.status === 'error'
                ? 'bg-destructive/5 border-destructive/30'
                : 'bg-primary/10 border-primary/20'
            )}
          >
            {/* Show text content only - no attachment previews */}
            {message.content && (
              <div className="whitespace-pre-wrap break-words leading-relaxed">
                {message.content}
              </div>
            )}
          </div>
        </div>

        {/* Failed-send retry row */}
        {message.status === 'error' && onRetry && (
          <div className="flex justify-end mt-1.5 gap-2 items-center">
            <span className="text-xs text-destructive/80">Failed to send</span>
            <Tooltip content="Retry sending this message" side="top" delay={300}>
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
                  'text-destructive hover:bg-destructive/10 transition-colors'
                )}
                aria-label="Retry sending message"
              >
                <Icon iconType="RotateCcw" size="sm" />
                Retry
              </button>
            </Tooltip>
          </div>
        )}

        {/* Timestamp - only shown at time boundaries */}
        {showTimestamp && message.timestamp && (
          <div className="flex justify-end mt-1">
            <Tooltip content={formatAbsoluteTime(message.timestamp)} side="top" delay={400}>
              <span
                className="text-[10px] text-muted-foreground/60
 whitespace-nowrap cursor-default select-none"
              >
                {formatRelativeTime(message.timestamp)}
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }

  // Assistant messages: plain text, left-aligned, no bubble
  // Show loading indicator for processing/streaming messages with no content
  if (showLoading) {
    // Check if message content contains status indicator (wrapped in underscores)
    const statusMatch = message.content?.match(/^_(.+)_$/);
    const statusText = statusMatch ? statusMatch[1] : null;

    return (
      <div
        ref={onRef}
        className={cn(
          'flex w-full mb-2 justify-start rounded-lg transition-all duration-300',
          'animate-in fade-in slide-in-from-left-2 duration-300',
          isHighlighted && 'bg-primary/10 ring-2 ring-primary/30 shadow-lg'
        )}
      >
        <TypingIndicator label={statusText ?? undefined} />
      </div>
    );
  }

  // Regular assistant messages: plain text, left-aligned, no bubble
  // Note: Intentionally ignoring any experimental_attachments to prevent server image previews
  return (
    <div
      ref={onRef}
      className={cn(
        'w-full mb-2 group rounded-lg transition-all duration-300 relative',
        isHighlighted && 'bg-primary/10 ring-2 ring-primary/30 shadow-lg'
      )}
    >
      {/* Hover-reveal timestamp — top-right of assistant messages when no boundary timestamp is shown */}
      {!showTimestamp && message.timestamp && (
        <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-auto">
          <Tooltip content={formatAbsoluteTime(message.timestamp)} side="left" delay={400}>
            <span className="text-[10px] text-muted-foreground/40 whitespace-nowrap select-none cursor-default">
              {formatRelativeTime(message.timestamp)}
            </span>
          </Tooltip>
        </div>
      )}
      <div className="w-full text-sm text-foreground prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="mb-3 mt-0 text-2xl font-bold leading-tight">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="mb-2 mt-0 text-xl font-semibold leading-tight">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="mb-2 mt-0 text-lg font-semibold leading-tight">{children}</h3>
            ),
            h4: ({ children }) => (
              <h4 className="mb-2 mt-0 text-base font-semibold leading-tight">{children}</h4>
            ),
            h5: ({ children }) => (
              <h5 className="mb-2 mt-0 text-sm font-semibold leading-tight">{children}</h5>
            ),
            h6: ({ children }) => (
              <h6 className="mb-2 mt-0 text-xs font-semibold leading-tight">{children}</h6>
            ),
            hr: () => <hr className="my-4 border-border opacity-50" />,
            strong: ({ children }) => (
              <strong className="font-bold text-foreground">{children}</strong>
            ),
            em: ({ children }) => <em className="italic text-foreground/80">{children}</em>,
            table: ({ children }) => (
              <table className="border border-border/30 rounded-xl overflow-hidden w-full text-left glass-surface-bg glass-surface-border shadow-sm">
                {children}
              </table>
            ),
            thead: ({ children }) => <thead className="glass-surface-hover">{children}</thead>,
            th: ({ children }) => (
              <th className="border border-border/30 px-3 py-2 font-semibold">{children}</th>
            ),
            td: ({ children }) => <td className="border border-border/30 px-3 py-2">{children}</td>,
            p: ({ children }) => (
              <p className="mb-2 last:mb-0 whitespace-pre-wrap break-words leading-relaxed">
                {children}
              </p>
            ),
            ul: ({ children }) => <ul className="mb-2 last:mb-0 pl-4 list-disc">{children}</ul>,
            ol: ({ children }) => <ol className="mb-2 last:mb-0 pl-4 list-decimal">{children}</ol>,
            li: ({ children }) => <li className="mb-1">{children}</li>,
            code: ({ children }) => (
              <code className="glass-surface px-1.5 py-0.5 rounded-md text-sm font-mono">
                {children}
              </code>
            ),
            pre: ({ children }) => (
              <pre className="glass-surface p-3 rounded-xl mb-2 overflow-x-auto shadow-sm">
                {children}
              </pre>
            ),
            a: ({ children, href, ...props }) => {
              const handleLinkClick = (e: React.MouseEvent) => {
                e.preventDefault();
                if (href && onLinkClick) {
                  onLinkClick(href);
                }
              };

              return (
                <a
                  {...props}
                  href={href}
                  onClick={handleLinkClick}
                  className="underline underline-offset-4 cursor-pointer hover:text-primary transition-colors"
                >
                  {children}
                </a>
              );
            },
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 pl-4 italic opacity-90">{children}</blockquote>
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>

        {/* Voice Controls + Copy + Regenerate — only show for assistant messages with content */}
        {isAssistant && message.content && (
          <div className="mt-2 flex items-center gap-2">
            <VoiceMessageControls text={message.content} />
            {!isStreaming && (
              <>
                <Tooltip content={copied ? 'Copied!' : 'Copy message'} side="right" delay={300}>
                  <IconButton
                    iconName={copied ? 'Check' : 'Copy'}
                    size="sm"
                    variant="ghost"
                    onClick={handleCopy}
                    aria-label={copied ? 'Copied' : 'Copy message'}
                    className={cn(
                      'transition-all duration-200',
                      copied
                        ? 'opacity-100 text-green-500 hover:text-green-500'
                        : 'opacity-0 group-hover:opacity-100'
                    )}
                  />
                </Tooltip>
                {isLastAssistant && onRegenerate && (
                  <Tooltip content="Regenerate response" side="right" delay={300}>
                    <IconButton
                      iconName="RotateCcw"
                      size="sm"
                      variant="ghost"
                      onClick={onRegenerate}
                      aria-label="Regenerate response"
                      className="opacity-0 group-hover:opacity-100 transition-all duration-200"
                    />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Timestamp - only shown at time boundaries */}
      {showTimestamp && message.timestamp && (
        <div className="mt-2">
          <Tooltip content={formatAbsoluteTime(message.timestamp)} side="top" delay={400}>
            <span
              className="text-[10px] text-muted-foreground/60
 whitespace-nowrap cursor-default select-none"
            >
              {formatRelativeTime(message.timestamp)}
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const ChatLog = React.forwardRef<HTMLTextAreaElement, ChatLogProps>(
  (
    {
      messages,
      sessionId,
      targetMessageId,
      value,
      placeholder = 'Type a message...',
      disabled = false,
      isLoading = false,
      isLoadingMessages = false,
      showEmptyState = false,
      className,
      hideCloseButton = false,
      hasPendingScreenshot,
      attachedFiles = [],
      isProcessingFiles = false,
      isCapturingScreen = false,
      onChange,
      onSubmit,
      onUpload,
      onCapture,
      onCapturePageHtml,
      isCapturingPage = false,
      draftPageContext,
      onClearPageContext,
      onClose,
      onRemoveAttachment,
      onFilesDrop,
      onFilesPaste,
      onGoToIntegrations,
      onRegenerate,
      onRetry,
      selectedSkill,
      onOpenSkillSelector,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const voiceRecorderRef = useRef<VoiceRecorderButtonHandle>(null);
    const enterIconButtonRef = useRef<HTMLButtonElement>(null!);
    const promptsButtonRef = useRef<HTMLButtonElement>(null!);
    const suggestButtonRef = useRef<HTMLButtonElement>(null!);
    const virtuosoRef = useRef<VirtuosoHandle>(null); // Virtuoso ref for scroll control
    const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map()); // Track message elements
    const [highlightedMessageId, setHighlightedMessageId] = React.useState<string | null>(null);
    const lastSessionIdRef = useRef<string | undefined>(sessionId); // Track session changes
    const lastScrolledToMessageIdRef = useRef<string | null>(null); // Track which message we've already scrolled to
    const [initialTopMostItemIndex, setInitialTopMostItemIndex] = React.useState<number>(999999); // Start at bottom
    const [showScrollToBottom, setShowScrollToBottom] = React.useState(false); // Track if scroll-to-bottom button should show
    const [hasNewMessages, setHasNewMessages] = React.useState(false); // Unread indicator on scroll button
    const [justAte, setJustAte] = React.useState(false); // Triggers "nom" animation on successful page capture
    const prevIsCapturingPageRef = useRef(false);
    /** Virtuoso followOutput does not track height growth of existing rows (streaming). Synced in handleAtBottomStateChange. */
    const isAtBottomRef = useRef(true);
    const lastSeenMessageIdRef = useRef<string | undefined>(undefined);
    /** Avoid smooth scroll on every streaming tick; reset when session changes. */
    const lastAutoScrolledAssistantIdRef = useRef<string | null>(null);
    const prevLastMessageIdRef = useRef<string | undefined>(undefined);

    // Memoized ref callback to prevent creating new functions on every render
    const handleMessageRef = React.useCallback(
      (messageId: string) => (el: HTMLDivElement | null) => {
        if (el) {
          messageRefs.current.set(messageId, el);
        } else {
          messageRefs.current.delete(messageId);
        }
      },
      []
    );

    // Image preview state
    const [imagePreview, setImagePreview] = React.useState<{
      isOpen: boolean;
      imageUrl: string;
      imageName: string;
    }>({ isOpen: false, imageUrl: '', imageName: '' });

    // Prompt library state
    const [isPromptsDropdownOpen, setIsPromptsDropdownOpen] = React.useState(false);
    const [promptVariableDialog, setPromptVariableDialog] = React.useState<{
      isOpen: boolean;
      prompt: PromptTemplate | null;
    }>({ isOpen: false, prompt: null });
    const [showSuggestionsPanel, setShowSuggestionsPanel] = React.useState(false);

    // Streaming explanation tooltip state
    const [showStreamingTooltip, setShowStreamingTooltip] = React.useState(false);

    // ✅ CORRECT - Use selectors, not destructuring (per .cursorrules)
    const addToRecentlyUsed = usePromptLibraryStore(state => state.addToRecentlyUsed);
    const trackUsage = usePromptLibraryStore(state => state.trackUsage);

    // Platform-aware prompt library opener
    const openPromptLibrary = useOpenPromptLibrary();
    const { blockedFeature, showBlockedFeature, clearBlockedFeature } = useBlockedFeatureModal();

    // Dynamic suggestions hook
    const {
      suggestions,
      isLoading: isSuggestionsLoading,
      generateSuggestions,
      clearSuggestions,
    } = useDynamicSuggestions(
      hasPendingScreenshot
        ? {
            screenCaptureId: 'current-capture',
            recentMessages: messages.slice(-3).map(m => m.content),
          }
        : {
            recentMessages: messages.slice(-3).map(m => m.content),
          }
    );

    // MCP select state - initialize from localStorage
    const [isMcpSelectOpen, setIsMcpSelectOpen] = React.useState(false);
    const [selectedAccountId, setSelectedAccountId] = React.useState<string | undefined>(() => {
      // Initialize from localStorage on first render
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage.getItem('selectedMcpAccountId') || undefined;
        }
      } catch (error) {
        logger.error('Failed to load selected MCP account from localStorage:', error);
      }
      return undefined;
    });

    const { hasExtension } = useExtensionDetection();
    const autoScroll = useAutoScroll();
    // Get integrations to determine button visibility and state
    const { data: integrationsData } = useGetIntegrationsQuery();

    // Auto-switch MCP account when Real-Time Mode resolves a match.
    // The RTM resolver fans out across all org accounts and returns the
    // one that owns the matched patient — we switch to it automatically
    // so the AI chat is already talking to the right practice.
    //
    // Depends on both `suggestedMcpAccountId` and `integrationsData` so
    // the switch fires even if integrations load after the suggestion
    // arrives (race condition: RTM resolves before the query settles).
    const suggestedMcpAccountId = useRealTimeModeSuggestedMcpAccountId();
    React.useEffect(() => {
      if (!suggestedMcpAccountId) return;
      if (suggestedMcpAccountId === selectedAccountId) return;
      if (!integrationsData?.body?.integrations) return; // wait for list to load

      const matchedOrg = integrationsData.body.integrations.find(
        int => int.account_id === suggestedMcpAccountId
      );
      if (!matchedOrg) return; // account no longer in org — ignore stale suggestion

      setSelectedAccountId(suggestedMcpAccountId);
      toast.info(
        `Enter account switched to ${matchedOrg.business_name} to match the open patient chart.`
      );
    }, [suggestedMcpAccountId, integrationsData]); // selectedAccountId intentionally omitted — only fire when suggestion changes

    // Integration management (add account) is admin-only, but members can still
    // select an existing account. The Enter button shows for admins (so they can
    // add the first integration) or whenever at least one integration exists.
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const hasIntegrations = React.useMemo(() => {
      return (integrationsData?.body?.integrations?.length ?? 0) > 0;
    }, [integrationsData]);

    // Validate selected account when integrations data changes
    React.useEffect(() => {
      if (selectedAccountId && integrationsData?.body?.integrations) {
        // Check if the selected account still exists in the integrations list
        const accountExists = integrationsData.body.integrations.some(
          int => int.account_id === selectedAccountId
        );
        if (!accountExists) {
          // Clear invalid account
          setSelectedAccountId(undefined);
          try {
            localStorage.removeItem('selectedMcpAccountId');
          } catch (error) {
            logger.error('Failed to clear invalid MCP account from localStorage:', error);
          }
        }
      }
    }, [selectedAccountId, integrationsData]);

    // Persist selected account to localStorage when it changes
    React.useEffect(() => {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          if (selectedAccountId) {
            localStorage.setItem('selectedMcpAccountId', selectedAccountId);
          } else {
            localStorage.removeItem('selectedMcpAccountId');
          }
          // Mirror to the store so views outside this component (Tasks) can
          // react to a practice change instead of reading a stale localStorage
          // value. chat-log stays the owner; this only publishes.
          setSelectedEnterAccountId(selectedAccountId);
        }
      } catch (error) {
        logger.error('Failed to save selected MCP account to localStorage:', error);
      }
    }, [selectedAccountId]);

    // Show streaming explanation tooltip when AI starts streaming
    React.useEffect(() => {
      const hasStreamingMessage = messages.some(
        msg =>
          (msg.status === 'streaming' || msg.status === 'processing') &&
          ((msg as any).role === 'assistant' || (msg as any).sender === 'ai')
      );

      if (hasStreamingMessage) {
        setShowStreamingTooltip(true);
      } else {
        setShowStreamingTooltip(false);
      }
    }, [messages]);

    // Detect new completed assistant messages while user is scrolled up → show unread dot
    React.useEffect(() => {
      if (messages.length === 0) return;
      const last = messages[messages.length - 1];
      if (!last) return;
      const role = (last as any).role || (last as any).sender;
      const isAssistant = role === 'assistant' || role === 'ai';
      const isComplete = last.status !== 'streaming' && last.status !== 'processing';
      // Only flag as new if this is a different message than last time AND user is scrolled up
      if (isAssistant && isComplete && last.id !== lastSeenMessageIdRef.current) {
        lastSeenMessageIdRef.current = last.id;
        if (!isAtBottomRef.current) {
          setHasNewMessages(true);
        }
      }
    }, [messages]);

    // Fire the "nom" animation when page capture completes (isCapturingPage: true → false + draftPageContext set)
    React.useEffect(() => {
      if (prevIsCapturingPageRef.current && !isCapturingPage && draftPageContext) {
        setJustAte(true);
        const t = setTimeout(() => setJustAte(false), 600);
        return () => clearTimeout(t);
      }
      prevIsCapturingPageRef.current = isCapturingPage;
      return undefined;
    }, [isCapturingPage, draftPageContext]);

    // Scroll to target message when targetMessageId changes (from search results)
    // ONE-TIME ONLY - don't re-scroll if user manually scrolls away
    React.useEffect(() => {
      if (targetMessageId && !isLoadingMessages && messages.length > 0) {
        // Only scroll if we haven't already scrolled to this specific message
        if (lastScrolledToMessageIdRef.current === targetMessageId) {
          return undefined; // Already scrolled to this message, don't re-scroll
        }

        // Small delay to ensure Virtuoso is fully rendered
        const timer = setTimeout(() => {
          const messageIndex = messages.findIndex(m => m.id === targetMessageId);
          if (messageIndex !== -1 && virtuosoRef.current) {
            // Scroll to the message with smooth animation
            virtuosoRef.current.scrollToIndex({
              index: messageIndex,
              align: 'center',
              behavior: 'smooth',
            });

            // Highlight the message
            setHighlightedMessageId(targetMessageId);

            // Mark this message as scrolled to (prevents re-scrolling)
            lastScrolledToMessageIdRef.current = targetMessageId;

            // Remove highlight after 3 seconds
            setTimeout(() => {
              setHighlightedMessageId(null);
            }, 3000);

            logger.info(
              '📜 [ChatLog] Scrolled to search result (one-time):',
              targetMessageId,
              'at index:',
              messageIndex
            );
          }
        }, 200);

        return () => clearTimeout(timer);
      }
      return undefined;
    }, [targetMessageId, isLoadingMessages, messages]);

    // Auto-scroll to bottom when opening a new session (not from search)
    React.useEffect(() => {
      const sessionChanged = sessionId !== lastSessionIdRef.current;

      if (sessionChanged) {
        lastSessionIdRef.current = sessionId;
        // Reset the scroll-to-message tracker when session changes
        lastScrolledToMessageIdRef.current = null;
        lastAutoScrolledAssistantIdRef.current = null;
        prevLastMessageIdRef.current = undefined;

        // If not from search and we have messages, scroll to bottom
        if (autoScroll && !targetMessageId && messages.length > 0 && !isLoadingMessages) {
          // Use manual scroll to bottom on session change
          const timer = setTimeout(() => {
            if (virtuosoRef.current) {
              virtuosoRef.current.scrollToIndex({
                index: 'LAST',
                align: 'end',
                behavior: 'auto', // Use auto for instant scroll on session change
              });
              logger.info('📜 [ChatLog] Auto-scrolled to bottom for session:', sessionId);
            }
          }, 150); // Slightly increased delay to ensure Virtuoso is ready

          return () => clearTimeout(timer);
        }
      }
      return undefined;
    }, [sessionId, targetMessageId, messages.length, isLoadingMessages, autoScroll]);

    // Scroll the last assistant message into view (DOM scrollIntoView), like a plain ref + useEffect.
    // Virtuoso's followOutput does not follow row height growth during streaming; this does.
    const lastMessage = messages[messages.length - 1];
    const lastMessageFollowKey = lastMessage
      ? `${lastMessage.id}:${lastMessage.content?.length ?? 0}:${lastMessage.status ?? ''}`
      : '';

    React.useLayoutEffect(() => {
      if (!autoScroll) return;
      if (isLoadingMessages || showEmptyState || messages.length === 0) return;
      if (targetMessageId) return;
      if (!lastMessage) return;

      const isNewLastMessage = prevLastMessageIdRef.current !== lastMessage.id;
      prevLastMessageIdRef.current = lastMessage.id;

      const messageRole =
        (lastMessage as { role?: string; sender?: string }).role ??
        (lastMessage as { sender?: string }).sender;
      const isAssistant = messageRole === 'assistant' || messageRole === 'ai';
      if (!isAssistant) return;

      // Follow streaming only while pinned to bottom; always jump once when the last message id changes (new reply bubble).
      if (!isAtBottomRef.current && !isNewLastMessage) return;

      const scrollLastResponseIntoView = () => {
        const isFirstPaintForMessage = lastAutoScrolledAssistantIdRef.current !== lastMessage.id;
        lastAutoScrolledAssistantIdRef.current = lastMessage.id;

        const behavior: ScrollBehavior = isFirstPaintForMessage ? 'smooth' : 'auto';

        virtuosoRef.current?.scrollToIndex({
          index: 'LAST',
          align: 'end',
          behavior,
        });
      };

      const id = requestAnimationFrame(scrollLastResponseIntoView);

      return () => cancelAnimationFrame(id);
    }, [
      autoScroll,
      lastMessageFollowKey,
      messages.length,
      isLoadingMessages,
      showEmptyState,
      targetMessageId,
    ]);

    // External links hook for opening links in browser
    const { openInBrowser } = useExternalLinks();

    // Efficiently select all attachment data once at the top level
    // Pass sessionId to enable retrieval of images from bucket store for historical messages
    const messageAttachments = useMessageAttachments(messages, sessionId);

    // Drag and drop functionality
    const { dragState, getDragProps } = useDragAndDrop(
      {
        onFilesDrop: (files: File[]) => {
          if (onFilesDrop && !disabled && !isLoading && !isProcessingFiles) {
            onFilesDrop(files);
          }
        },
        onError: (error: string) => {
          logger.error('Drag and drop error:', error);
        },
      },
      {
        disabled: disabled || isLoading || isProcessingFiles,
        maxFiles: 5,
        supportMixedFileTypes: true,
      }
    );

    // Merge internal ref with forwarded ref
    const mergedTextareaRef = React.useCallback(
      (node: HTMLTextAreaElement) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from bubbling up to parent elements

        if (!disabled && value.trim()) {
          onSubmit?.(value);
          // Clear the input after submitting
          onChange?.('');
        }
      }
      // Shift+Enter allows new lines
    }

    function handleSubmit() {
      if (!disabled && value.trim()) {
        onSubmit?.(value);
        // Clear the input after submitting
        onChange?.('');
      }
    }

    function handleUpload() {
      onUpload?.();
    }

    function handleVoiceRecord() {
      textareaRef.current?.focus();
      void voiceRecorderRef.current?.start();
    }

    function handleCapture() {
      onCapture?.();
    }

    function handleCapturePageHtml() {
      onCapturePageHtml?.();
    }

    function handleClose() {
      // Clear the input when closing
      onChange?.('');
      onClose?.();
    }

    const handleImageClick = (imageUrl: string, imageName: string) => {
      setImagePreview({ isOpen: true, imageUrl, imageName });
    };

    const handleFileClick = async (file: ProcessedAttachment) => {
      logger.info('🔍 [handleFileClick] File clicked:', {
        name: file.name,
        contentType: file.contentType,
        isImage: file.contentType.startsWith('image/'),
        url: file.url,
      });

      if (file.contentType.startsWith('image/')) {
        // For images, show in modal
        logger.info('🔍 [handleFileClick] Showing image in modal');
        handleImageClick(file.url, file.name);
      } else if (file.contentType === 'application/pdf') {
        try {
          // Create a temporary download link to save the PDF locally
          const response = await fetch(file.url);
          const blob = await response.blob();

          // Create a download link
          const downloadLink = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          downloadLink.href = objectUrl;
          downloadLink.download = file.name.endsWith('.pdf') ? file.name : `${file.name}.pdf`;

          // Trigger the download
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up the object URL after download
          setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
          }, 1000);

          logger.info(`PDF downloaded: ${file.name}`);
        } catch (error) {
          logger.error('Failed to download PDF:', error);
          // Fallback: try opening the data URL directly in a new window
          window.open(file.url, '_blank');
        }
      } else {
        // For other files, try to download them as well
        logger.info('File preview clicked:', file.name, file.contentType);
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();

          // Create a download link
          const downloadLink = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          downloadLink.href = objectUrl;
          downloadLink.download = file.name;

          // Trigger the download
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          // Clean up the object URL after download
          setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
          }, 1000);

          logger.info(`File downloaded: ${file.name}`);
        } catch (error) {
          logger.error('Failed to download file:', error);
          // Fallback: open in new window
          window.open(file.url, '_blank');
        }
      }
    };

    const handleCloseImagePreview = () => {
      setImagePreview({ isOpen: false, imageUrl: '', imageName: '' });
    };

    // Prompt library handlers
    const handlePromptSelect = (prompt: PromptTemplate) => {
      // Track usage
      trackUsage(prompt.id);
      addToRecentlyUsed(prompt.id);

      // If prompt has variables, show dialog
      if (prompt.variables && prompt.variables.length > 0) {
        setPromptVariableDialog({ isOpen: true, prompt });
      } else {
        // No variables, insert directly
        handlePromptInsert(prompt.content);
      }
    };

    const handlePromptInsert = (content: string) => {
      // Insert prompt content into textarea
      const currentValue = value || '';
      const newValue = currentValue ? `${currentValue}\n\n${content}` : content;
      onChange?.(newValue);

      // Focus textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };

    const handleOpenFullLibrary = () => {
      openPromptLibrary(showBlockedFeature);
    };

    // Dynamic suggestions handlers
    const handleGenerateSuggestions = async () => {
      await generateSuggestions();
      setShowSuggestionsPanel(true);
    };

    const handleSelectSuggestion = (suggestion: DynamicSuggestion) => {
      // Insert suggestion prompt
      handlePromptInsert(suggestion.prompt);

      // Close suggestions panel
      setShowSuggestionsPanel(false);
    };

    const handleCloseSuggestions = () => {
      setShowSuggestionsPanel(false);
      clearSuggestions();
    };

    // Focus the textarea when the component mounts
    React.useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, []);

    // Auto-grow textarea height based on content
    React.useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.style.height = 'auto';

      const next = Math.min(
        TEXTAREA_MAX_HEIGHT,
        Math.max(TEXTAREA_MIN_HEIGHT, textarea.scrollHeight)
      );

      textarea.style.height = `${next}px`;
      const windowMinHeight = next + WINDOW_MIN_HEIGHT_ADJUSTMENT;
      textarea.style.overflowY = next >= TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';

      adjustWindowHeight(windowMinHeight);
    }, [value]);

    // Handle scroll to bottom button click
    const handleScrollToBottom = () => {
      if (virtuosoRef.current && messages.length > 0) {
        virtuosoRef.current.scrollToIndex({
          index: 'LAST',
          align: 'end',
          behavior: 'smooth',
        });
        setShowScrollToBottom(false);
        setHasNewMessages(false);
      }
    };

    // Track when user scrolls away from bottom
    const handleAtBottomStateChange = (atBottom: boolean) => {
      isAtBottomRef.current = atBottom;
      if (atBottom) setHasNewMessages(false);
      setShowScrollToBottom(
        !atBottom && messages.length > 0 && !showEmptyState && !isLoadingMessages
      );
    };

    return (
      <div
        className={cn(
          'w-full h-full flex flex-col relative',
          dragState.isDragActive && 'ring-2 ring-primary ring-opacity-50',
          className
        )}
        data-testid="chat-log"
        onDragEnter={e => getDragProps().onDragEnter(e.nativeEvent)}
        onDragLeave={e => getDragProps().onDragLeave(e.nativeEvent)}
        onDragOver={e => getDragProps().onDragOver(e.nativeEvent)}
        onDrop={e => getDragProps().onDrop(e.nativeEvent)}
      >
        {/* Drag and Drop Overlay */}
        {dragState.isDragActive && (
          <div
            className={cn(
              'absolute inset-0 z-50 flex items-center justify-center pointer-events-none',
              'bg-background/90 backdrop-blur-sm',
              'border-2 border-dashed rounded-md',
              dragState.isDragReject
                ? 'border-destructive bg-destructive/5 text-destructive'
                : 'border-primary bg-primary/5 text-primary'
            )}
          >
            <div className="text-center">
              <div className="text-2xl mb-2">{dragState.isDragReject ? '❌' : '📎'}</div>
              <div className="text-lg font-medium mb-1">
                {dragState.isDragReject
                  ? 'Invalid files'
                  : dragState.draggedFiles.length > 1
                    ? `Drop ${dragState.draggedFiles.length} files`
                    : 'Drop file here'}
              </div>
              {dragState.isDragReject && (
                <div className="text-sm opacity-75">Only image and PDF files are supported</div>
              )}
            </div>
          </div>
        )}
        {/* Messages area - grows to fill available space above input */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isLoadingMessages ? (
            <div className="h-full overflow-y-auto custom-scrollbar">
              <ChatLoadingSkeleton />
            </div>
          ) : showEmptyState || messages.length === 0 ? (
            <div className="h-full overflow-y-auto custom-scrollbar">
              {showEmptyState ? (
                <StarterPrompts
                  {...(selectedSkill?.starterPrompts
                    ? { skillStarterPrompts: selectedSkill.starterPrompts }
                    : {})}
                  {...(selectedSkill?.name ? { activeSkillName: selectedSkill.name } : {})}
                  onPromptClick={prompt => {
                    if (onSubmit) {
                      onSubmit(prompt);
                    }
                    if (selectedSkill) {
                      import('../services/analytics').then(({ mixpanel }) => {
                        mixpanel.track('Skill Starter Prompt Used', {
                          skillId: selectedSkill.id,
                          skillName: selectedSkill.name,
                        });
                      });
                    }
                  }}
                />
              ) : (
                <NewChatHome
                  selectedSkill={selectedSkill}
                  {...(onOpenSkillSelector ? { onOpenSkillSelector } : {})}
                  {...(onUpload ? { onUpload } : {})}
                  {...(hasExtension ? { onVoiceRecord: handleVoiceRecord } : {})}
                  {...(isAdmin && onGoToIntegrations ? { onGoToIntegrations } : {})}
                />
              )}
            </div>
          ) : (
            <Virtuoso
              ref={virtuosoRef}
              data={messages}
              followOutput={false}
              alignToBottom
              initialTopMostItemIndex={initialTopMostItemIndex}
              atBottomStateChange={handleAtBottomStateChange}
              atBottomThreshold={50}
              className="custom-scrollbar"
              style={{ height: '100%' }}
              itemContent={(index, message) => {
                const attachments = messageAttachments.get(message.id) || [];
                const isHighlighted = highlightedMessageId === message.id;

                // Calculate if timestamp should be shown (grouped timestamps)
                let showTimestamp = false;
                if (index === 0 || index === messages.length - 1) {
                  // Always show timestamp for first and last message
                  showTimestamp = true;
                } else if (message.timestamp) {
                  const prevMessage = messages[index - 1];
                  if (prevMessage?.timestamp) {
                    const currentTime = new Date(message.timestamp).getTime();
                    const prevTime = new Date(prevMessage.timestamp).getTime();
                    const timeDiff = currentTime - prevTime;

                    // Show timestamp if:
                    // - More than 5 minutes gap (300000ms)
                    // - Different day
                    const fiveMinutes = 5 * 60 * 1000;
                    const currentDate = new Date(message.timestamp).toDateString();
                    const prevDate = new Date(prevMessage.timestamp).toDateString();

                    showTimestamp = timeDiff > fiveMinutes || currentDate !== prevDate;
                  }
                }

                // Show global loading indicator as the last item only when there's no assistant message with processing/streaming status
                const shouldShowGlobalLoading =
                  index === messages.length - 1 &&
                  isLoading &&
                  !messages.some(msg => {
                    const messageRole = (msg as any).role || (msg as any).sender;
                    const isAssistant = messageRole === 'assistant' || messageRole === 'ai';
                    return (
                      isAssistant && (msg.status === 'processing' || msg.status === 'streaming')
                    );
                  });

                const messageRole = (message as any).role || (message as any).sender;
                const isAssistantMsg = messageRole === 'assistant' || messageRole === 'ai';
                const isLastAssistant =
                  isAssistantMsg &&
                  index === messages.length - 1 &&
                  message.status !== 'streaming' &&
                  message.status !== 'processing';

                return (
                  <div className="px-3 sm:px-6 first:pt-3 sm:first:pt-6 last:pb-3 sm:last:pb-4">
                    <MessageBubble
                      key={message.id}
                      message={message}
                      attachments={attachments}
                      onImageClick={handleImageClick}
                      onFileClick={handleFileClick}
                      onLinkClick={openInBrowser}
                      isHighlighted={isHighlighted}
                      onRef={handleMessageRef(message.id)}
                      showTimestamp={showTimestamp && !shouldShowGlobalLoading}
                      sessionId={sessionId}
                      isLastAssistant={isLastAssistant}
                      onRegenerate={isLastAssistant ? onRegenerate : undefined}
                      onRetry={onRetry}
                    />

                    {/* Show global loading indicator after the last message */}
                    {shouldShowGlobalLoading && (
                      <div className="flex w-full mb-2 justify-start animate-in fade-in slide-in-from-left-2 duration-300">
                        <TypingIndicator />
                      </div>
                    )}

                    {/* Show timestamp after loading indicator for last message */}
                    {shouldShowGlobalLoading && showTimestamp && message.timestamp && (
                      <div className="mt-2">
                        <Tooltip
                          content={formatAbsoluteTime(message.timestamp)}
                          side="top"
                          delay={400}
                        >
                          <span
                            className="text-[10px] text-muted-foreground/60
 whitespace-nowrap cursor-default select-none"
                          >
                            {formatRelativeTime(message.timestamp)}
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          )}

          {/* Floating scroll to bottom button */}
          {showScrollToBottom && (
            <div className="absolute bottom-36 right-6 z-30">
              <div className="bg-background rounded-full animate-in fade-in-0 slide-in-from-bottom-4 duration-300 animate-bounce">
                <Tooltip
                  content={hasNewMessages ? 'New message below' : 'Scroll to bottom'}
                  side="left"
                  delay={300}
                >
                  <button
                    onClick={handleScrollToBottom}
                    className="relative flex items-center justify-center p-3 rounded-full
 bg-primary/20 text-primary hover:bg-primary/30
 transition-all duration-200 hover:scale-105 active:scale-95
 border border-primary/10
 w-12 h-12"
                    aria-label={
                      hasNewMessages ? 'New message — scroll to bottom' : 'Scroll to bottom'
                    }
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                    {/* Unread indicator dot */}
                    {hasNewMessages && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-primary" />
                      </span>
                    )}
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
        {/* Input area - fixed at bottom */}
        <div className="flex-shrink-0 py-1 px-1 pt-1 sm:py-2 sm:px-2 sm:pt-2">
          {/* Already attached files - confirmed and ready to send */}
          <AttachedFilesList
            attachments={attachedFiles}
            onRemove={index => onRemoveAttachment?.(index)}
          />

          {/* Chip row — page context chip (skill shown as Dynamic Island in chat-view) */}
          {draftPageContext && (
            <div className="flex items-center gap-1.5 px-1 pb-1 flex-wrap">
              <div className="flex items-center gap-1.5 bg-bg-neutral-7 rounded-lg px-2.5 py-1 text-xs text-text-secondary max-w-full">
                <span className="shrink-0">🌐</span>
                <span className="truncate max-w-[200px]" title={draftPageContext.url}>
                  {draftPageContext.title || draftPageContext.url}
                </span>
                <span className="shrink-0 text-text-tertiary">
                  · {(draftPageContext.charCount / 1000).toFixed(1)}k chars
                  {draftPageContext.wasTruncated ? ' (truncated)' : ''}
                </span>
                <Tooltip content="Remove page context" side="top" delay={300}>
                  <button
                    type="button"
                    aria-label="Remove page context"
                    onClick={() => onClearPageContext?.()}
                    className="shrink-0 ml-0.5 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    ×
                  </button>
                </Tooltip>
              </div>
            </div>
          )}

          {/* AI Suggestions Panel */}
          <PromptSuggestionsPanel
            suggestions={suggestions}
            isVisible={showSuggestionsPanel}
            onClose={handleCloseSuggestions}
            onSelectSuggestion={handleSelectSuggestion}
          />

          {/* Input container with card background and border */}
          <div
            className={cn(
              'relative glass-surface-static-bg glass-surface-border rounded-2xl p-2 sm:p-4 transition-all duration-300',
              'shadow-lg hover:shadow-xl',
              // Add subtle glow when focused
              value.trim() && 'ring-1 ring-primary/20'
            )}
          >
            {/* Textarea - seamless part of the glass window */}
            <Textarea
              ref={mergedTextareaRef}
              placeholder={selectedSkill?.placeholder ?? placeholder}
              value={value}
              onChange={e => onChange?.(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={async e => {
                // Allow regular text paste when no files present
                const result = await processClipboardData(e.nativeEvent);
                if (result.files.length > 0) {
                  e.preventDefault();
                  if (onFilesPaste && !disabled && !isLoading && !isProcessingFiles) {
                    onFilesPaste(result.files);
                  }
                }
                // Otherwise let normal text paste proceed
              }}
              disabled={disabled || isLoading}
              className={cn(
                'w-full resize-none border-0 bg-transparent opacity-80',
                'focus-visible:ring-0 focus-visible:ring-offset-0',
                'leading-6',
                'pb-12' // Add padding at bottom for buttons
              )}
              style={{ minHeight: `${TEXTAREA_MIN_HEIGHT}px` }}
            />

            {/* Action buttons - positioned inside textarea at bottom right */}
            <div className="absolute bottom-4 sm:bottom-6 right-4 sm:right-7 flex items-center justify-end gap-0.5 sm:gap-1 scale-90 sm:scale-100">
              {/* Voice recorder button — not available in extension context */}
              {hasExtension && (
                <VoiceRecorderButton
                  ref={voiceRecorderRef}
                  onTranscriptionComplete={text => onChange?.(text)}
                />
              )}
              {/* Enter MCP button — admins always (can add the first integration
                  even with none configured); members only when an integration
                  already exists (to select an account). The "+" add-account
                  affordance inside the dropdown is admin-only. */}
              {(isAdmin || hasIntegrations) && (
                <>
                  <Tooltip content="Select Enter account" side="top" delay={300}>
                    <IconButton
                      ref={enterIconButtonRef}
                      aria-label="Select enter account"
                      variant={isMcpSelectOpen || selectedAccountId ? 'default' : 'ghost'}
                      size="sm"
                      iconSize="md"
                      iconName="EnterIcon"
                      onClick={() => setIsMcpSelectOpen(!isMcpSelectOpen)}
                      className={cn(
                        isMcpSelectOpen || selectedAccountId
                          ? 'text-text-secondary bg-bg-neutral-7'
                          : '',
                        'text-text-secondary hover:bg-bg-neutral-7'
                      )}
                    />
                  </Tooltip>

                  {/* MCP Select Dropdown */}
                  <McpSelect
                    isOpen={isMcpSelectOpen}
                    onClose={() => setIsMcpSelectOpen(false)}
                    {...(isAdmin ? { onAddAccount: () => onGoToIntegrations?.() } : {})}
                    selectedAccountId={selectedAccountId}
                    onSelectAccount={setSelectedAccountId}
                    anchorRef={enterIconButtonRef}
                  />
                </>
              )}
              {/* Page HTML capture button */}
              {onCapturePageHtml && (
                <Tooltip
                  content={
                    isCapturingPage
                      ? 'Capturing page...'
                      : justAte
                        ? 'Nom nom! Page eaten 🍽️'
                        : draftPageContext
                          ? 'Page eaten — click to refresh'
                          : 'Eat page content'
                  }
                  side="top"
                  delay={justAte ? 0 : 300}
                  disabled={isCapturingPage}
                >
                  <IconButton
                    aria-label={isCapturingPage ? 'Capturing page...' : 'Eat page content'}
                    variant={draftPageContext ? 'default' : 'ghost'}
                    size="sm"
                    iconSize="md"
                    iconName={isCapturingPage ? 'LoaderCircle' : 'Globe'}
                    onClick={handleCapturePageHtml}
                    disabled={isCapturingPage}
                    className={cn(
                      draftPageContext
                        ? 'text-text-primary bg-bg-neutral-7'
                        : 'text-text-secondary hover:bg-bg-neutral-7',
                      isCapturingPage && 'animate-spin',
                      justAte && 'animate-nom'
                    )}
                  />
                </Tooltip>
              )}

              {/* Screen capture button */}
              {hasExtension && onCapture && (
                <Tooltip
                  content={isCapturingScreen ? 'Capturing screen...' : 'Capture screen'}
                  side="top"
                  delay={300}
                  disabled={isCapturingScreen}
                >
                  <IconButton
                    aria-label={isCapturingScreen ? 'Capturing screen... 🔎' : 'Capture screen'}
                    variant={isCapturingScreen ? 'default' : 'ghost'}
                    size="sm"
                    iconSize="md"
                    iconName="ScreenCaptureIcon"
                    onClick={handleCapture}
                    disabled={isCapturingScreen}
                    className={cn(
                      isCapturingScreen
                        ? 'text-text-secondary bg-bg-neutral-7'
                        : 'text-text-secondary hover:bg-bg-neutral-7'
                    )}
                  />
                </Tooltip>
              )}

              {/* Upload button */}
              <Tooltip
                content={
                  isProcessingFiles ? 'Processing files...' : 'Attach files (images, documents)'
                }
                side="top"
                delay={300}
                disabled={isProcessingFiles}
              >
                <IconButton
                  aria-label={isProcessingFiles ? 'Processing files...' : 'Upload file'}
                  variant={isProcessingFiles ? 'default' : 'ghost'}
                  size="sm"
                  iconSize="md"
                  iconName="Upload"
                  onClick={handleUpload}
                  disabled={isProcessingFiles}
                  className={cn(
                    isProcessingFiles
                      ? 'text-text-secondary bg-bg-neutral-7'
                      : 'text-text-secondary hover:bg-bg-neutral-7'
                  )}
                />
              </Tooltip>

              {/* AI Specialist selector */}
              {onOpenSkillSelector && (
                <SkillSelectorButton
                  selectedSkill={selectedSkill}
                  onOpen={onOpenSkillSelector}
                  disabled={disabled || isLoading}
                />
              )}

              {/* Prompts button */}
              <Tooltip content="Browse prompts" side="top" delay={300}>
                <IconButton
                  ref={promptsButtonRef}
                  aria-label="Browse prompts"
                  variant={isPromptsDropdownOpen ? 'default' : 'ghost'}
                  size="sm"
                  iconSize="md"
                  iconName="Sparkles"
                  onClick={() => {
                    setIsPromptsDropdownOpen(!isPromptsDropdownOpen);
                    // Track browse prompts button click
                    if (!isPromptsDropdownOpen) {
                      import('../services/analytics').then(({ mixpanel }) => {
                        mixpanel.track('Browse Prompts Clicked', {
                          location: 'chat-input',
                        });
                      });
                    }
                  }}
                  className={cn(
                    isPromptsDropdownOpen
                      ? 'text-text-secondary bg-bg-neutral-7'
                      : 'text-text-secondary hover:bg-bg-neutral-7'
                  )}
                />
              </Tooltip>

              {/* Prompts Dropdown */}
              <PromptDropdownMenu
                isOpen={isPromptsDropdownOpen}
                onClose={() => setIsPromptsDropdownOpen(false)}
                onSelectPrompt={handlePromptSelect}
                onOpenFullLibrary={handleOpenFullLibrary}
                anchorRef={promptsButtonRef}
              />

              {/* AI Suggestions button */}
              <Tooltip
                content={
                  isSuggestionsLoading ? 'Generating suggestions...' : 'Get AI-suggested prompts'
                }
                side="top"
                delay={300}
                disabled={isSuggestionsLoading}
              >
                {isSuggestionsLoading ? (
                  <button
                    ref={suggestButtonRef}
                    aria-label="Generating AI suggestions"
                    disabled
                    className={cn(
                      'inline-flex items-center justify-center rounded-full h-8 w-8',
                      'bg-bg-neutral-7 text-text-secondary',
                      'transition-all duration-300',
                      'relative overflow-hidden'
                    )}
                  >
                    {/* Animated pulse background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent animate-shimmer" />
                    {/* Spinner */}
                    <svg
                      className="animate-spin h-4 w-4 relative z-10"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </button>
                ) : (
                  <IconButton
                    ref={suggestButtonRef}
                    aria-label="Get AI suggestions"
                    variant={showSuggestionsPanel ? 'default' : 'ghost'}
                    size="sm"
                    iconSize="md"
                    iconName="Lightbulb"
                    onClick={handleGenerateSuggestions}
                    className={cn(
                      showSuggestionsPanel
                        ? 'text-text-secondary bg-bg-neutral-7'
                        : 'text-text-secondary hover:bg-bg-neutral-7',
                      'transition-all duration-300'
                    )}
                  />
                )}
              </Tooltip>

              {/* Send/Submit button */}
              <Tooltip
                content={
                  disabled || isLoading || isProcessingFiles
                    ? 'Cannot send'
                    : !value.trim()
                      ? `Type a message · ⇧${getKeyDisplayText('ENTER')} for new line`
                      : `Send message · ${getKeyDisplayText('ENTER')}`
                }
                side="top"
                delay={300}
                disabled={disabled || isLoading || isProcessingFiles}
              >
                <IconButton
                  className="ml-1 sm:ml-2"
                  aria-label="Send message"
                  variant="submit"
                  size="sm"
                  iconSize="lg"
                  iconName="Send"
                  onClick={handleSubmit}
                  disabled={disabled || isLoading || isProcessingFiles || !value.trim()}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Image Preview Modal */}
        <ImagePreviewModal
          isOpen={imagePreview.isOpen}
          onClose={handleCloseImagePreview}
          imageUrl={imagePreview.imageUrl}
          imageName={imagePreview.imageName}
        />

        {/* Prompt Variable Dialog */}
        <PromptVariableDialog
          isOpen={promptVariableDialog.isOpen}
          onClose={() => setPromptVariableDialog({ isOpen: false, prompt: null })}
          prompt={promptVariableDialog.prompt}
          onSubmit={handlePromptInsert}
        />

        {/* Feature Blocked Modal (for sidebar) */}
        {blockedFeature && (
          <FeatureBlockedModal
            isOpen={true}
            onClose={clearBlockedFeature}
            featureName={blockedFeature.name}
            targetPath={blockedFeature.path}
          />
        )}
      </div>
    );
  }
);

ChatLog.displayName = 'ChatLog';
